import {useMemo, useState} from 'react';
import {ProductInDepotType} from '../types/ProductSliceTypes';
import {ResponseProductListInDepotType} from '../types/TypesStore';

export type SelectedProductType = ProductInDepotType & {buyQuantity: number};

export const useProductsHandler = (
	productsList?: ResponseProductListInDepotType,
) => {
	const [selectedProducts, setSelectedProducts] = useState<
		SelectedProductType[]
	>([]);
	const [filterByStock, setFilterByStock] = useState<boolean | null>(null);
	const [searchByName, setSearchByName] = useState<string>('');

	/**
	 * Обработчик изменения количества продукта
	 */
	const handleChangeProduct = (
		productId: string,
		action: '+' | '-' | 'manual' | 'remove',
		value?: number,
	) => {
		setSelectedProducts((prev) => {
			// Если действие 'remove' - устанавливаем количество в 0 (продукт удалится автоматически)
			if (action === 'remove') {
				action = 'manual';
				value = 0;
			}

			const existing = prev.find((p) => p.id === productId);
			const productFromList = productsList?.products?.find(
				(p) => p.id === productId,
			);

			// Если товар есть в selectedProducts - работаем с ним
			if (existing) {
				let newBuyQuantity = existing.buyQuantity;

				if (action === '+') {
					// Для товаров из заказа (которые могут не быть в productsList) разрешаем увеличение
					if (existing.count_store <= 0 && productFromList) {
						return prev; // нет остатка только если товар есть в productsList
					}
					newBuyQuantity++;
				}

				if (action === '-' && newBuyQuantity > 0) {
					newBuyQuantity--;
				}

				if (action === 'manual') {
					if (typeof value === 'number' && value >= 0) {
						const max = existing.count_store + existing.buyQuantity;
						newBuyQuantity = Math.min(value, max);
					}
				}

				if (newBuyQuantity === 0) {
					return prev.filter((p) => p.id !== productId);
				}

				const newCountStore = existing.count_store - (newBuyQuantity - existing.buyQuantity);

				return prev.map((p) =>
					p.id === productId
						? {
								...p,
								buyQuantity: newBuyQuantity,
								count_store: newCountStore,
							}
						: p,
				);
			}

			// Если товара нет в selectedProducts, но есть в productsList - добавляем его
			if (productFromList) {
				let newBuyQuantity = 0;

				if (action === '+') {
					if (productFromList.count_store <= 0) {
						return prev; // нет остатка
					}
					newBuyQuantity = 1;
				}

				if (action === 'manual') {
					if (typeof value === 'number' && value >= 0) {
						newBuyQuantity = Math.min(value, productFromList.count_store);
					}
				}

				if (newBuyQuantity === 0) {
					return prev; // не добавляем товар с количеством 0
				}

				const newCountStore = productFromList.count_store - newBuyQuantity;

				return [
					...prev,
					{
						...productFromList,
						buyQuantity: newBuyQuantity,
						count_store: newCountStore,
					},
				];
			}

			// Если товара нет ни в selectedProducts, ни в productsList - игнорим
			return prev;
		});
	};

	/**
	 * Обработчик изменения поискового запроса
	 */
	const handleChangeSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
		setSearchByName(event.currentTarget.value);
	};

	/**
	 * Обработчик фильтрации по наличию товара
	 */
	const handleFilterByStock = (filterValue: boolean | null) => {
		setFilterByStock(filterValue);
	};

	/**
	 * Обработчик клика на кнопку фильтрации в панели
	 */
	const handleClickButtonInPanel = (id: string) => {
		switch (id) {
			case 'all':
				handleFilterByStock(null);
				break;
			case 'inStock':
				handleFilterByStock(true);
				break;
			case 'selected':
				handleFilterByStock(false);
				break;
		}
	};

	// Получение отфильтрованного списка продуктов с мемоизацией
	const productsForRender = useMemo(() => {
		// Объединяем товары из productsList и selectedProducts
		// Приоритет у selectedProducts - если товар есть в selectedProducts, используем его данные
		const allProducts = [
			...(productsList?.products ?? []),
			...selectedProducts.filter(sp => !productsList?.products?.some(p => p.id === sp.id))
		];
		
		// Создаем Map, где приоритет у товаров из selectedProducts
		const productMap = new Map();
		
		// Сначала добавляем товары из productsList
		productsList?.products?.forEach(p => {
			productMap.set(p.id, p);
		});
		
		// Затем перезаписываем товарами из selectedProducts (если они есть)
		selectedProducts.forEach(sp => {
			productMap.set(sp.id, sp);
		});
		
		const uniqueProducts = Array.from(productMap.values());

		const filteredByQuery = uniqueProducts.filter(
			(p) =>
				!searchByName ||
				p.name.toLowerCase().includes(searchByName.toLowerCase()),
		);

		const withAdjusted = filteredByQuery.map((p) => {
			const q = selectedProducts.find((sp) => sp.id === p.id)?.buyQuantity ?? 0;
			const adjustedCount = Math.max(p.count_store - q, 0);
			return {
				...p,
				buyQuantity: q || undefined,
				count_store: adjustedCount,
			};
		});

		if (filterByStock === true) {
			// "В наявності" — показываем товары, у которых count_store > 0
			return withAdjusted.filter((p) => Number(p.count_store) > 0);
		}

		if (filterByStock === false) {
			// "Обрані" — показываем товары, которые уже выбраны (buyQuantity > 0)
			return withAdjusted.filter((p) => (p.buyQuantity ?? 0) > 0);
		}

		// Без фильтра — показываем все товары, включая те, что уже выбраны (даже с нулевым остатком)
		return withAdjusted.filter((p) => {
			// Показываем товары с visible_count === true ИЛИ товары, которые уже выбраны (для редактирования)
			const isSelected = selectedProducts.some(sp => sp.id === p.id && sp.buyQuantity > 0);
			return p.visible_count === true || isSelected;
		});
	}, [productsList?.products, selectedProducts, filterByStock, searchByName]);

	return {
		selectedProducts,
		setSelectedProducts,
		filterByStock,
		searchByName,
		setSearchByName,
		productsForRender,
		handleChangeProduct,
		handleChangeSearch,
		handleFilterByStock,
		handleClickButtonInPanel,
	};
};
